import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Flex,
  Text,
  Spinner
} from '@chakra-ui/react';
import {
  faArrowLeft,
  faEnvelope,
  faEye,
  faMinus,
  faPlus
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonIcon, ButtonText, OutlineCard } from '@morressier/alembic';
import {
  IDecisionHistoryItem,
  IRoleType,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import dateFormat from 'dateformat';
import { useState } from 'react';
import { Preview } from './ActivityTab/EmailViewModal';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useQuery } from '@tanstack/react-query';
import { fetchEmailByEventId } from 'src/api/queries';
import { Overlay } from '../Overlay';
import ShowAttachementFiles from './ShowAttachementFiles';
import { decisionStatusBadges } from '../Review/Badges';
import Divider from '../Divider';
import NavbarDashboard from '../NavbarDashboard';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
interface Props {
  decisionHistoryCount: number;
  decisionHistory: IDecisionHistoryItem[];
  onClick: () => void;
  submissionContextId: string;
  roleContext: IRoleType;
}
export const DecisionHistory = ({
  decisionHistoryCount,
  decisionHistory,
  onClick,
  submissionContextId,
  roleContext
}: Props) => {
  const { submissionId, submissionContext } = useSubmissionData();
  const { getFieldState } = useSubmissionFields();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailEventId, setEmailEventId] = useState('');
  const [showAttachments, setShowAttachments] = useState<
    ISubmissionFile[] | null
  >(null);
  const hasCOCEnabled = submissionContext?.config?.isCOCRecommendationRequired;
  const submittingAuthorEmail = getFieldState('SubmittingAuthor')?.value as
    | string
    | undefined;
  const {
    data: emailLog,
    isLoading,
    isError
  } = useQuery({
    queryKey: [
      'email-by-event',
      emailEventId,
      roleContext,
      submittingAuthorEmail
    ],
    queryFn: () =>
      fetchEmailByEventId(
        submissionId,
        emailEventId,
        roleContext,
        submittingAuthorEmail
      ),
    enabled: !!emailEventId && !!submittingAuthorEmail
  });
  return (
    <>
      {showAttachments && (
        <ShowAttachementFiles
          attachments={showAttachments}
          onClose={() => setShowAttachments(null)}
        />
      )}
      {emailEventId && isEmailModalOpen && (
        <Overlay>
          <NavbarDashboard submissionContextId={submissionContextId} />
          <Flex
            direction="column"
            minHeight={'full'}
            backgroundColor="surfaceSecondaryFill"
          >
            <Flex
              justifyContent="space-between"
              alignItems="center"
              p={6}
              pb={0}
            >
              <ButtonText
                variant="secondary"
                onClick={() => {
                  setEmailEventId('');
                  setIsEmailModalOpen(false);
                }}
                iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
              >
                Back to overview
              </ButtonText>
            </Flex>
            <Box flex={1} overflowY="auto" p={6}>
              <>
                {isLoading ? (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Spinner />
                  </Flex>
                ) : isError ? (
                  <Text color="red.500">Error loading email</Text>
                ) : (
                  <Preview emailLog={emailLog?.items} />
                )}
              </>
            </Box>
          </Flex>
        </Overlay>
      )}

      <Accordion allowToggle defaultIndex={0}>
        <AccordionItem
          border={hasCOCEnabled ? 'none' : '1px solid'}
          borderColor="neutralPrimaryBorder"
          borderRadius={hasCOCEnabled ? undefined : '10px'}
          overflow={hasCOCEnabled ? undefined : 'hidden'}
        >
          {({ isExpanded }) => (
            <>
              <AccordionButton
                padding={1}
                bgColor="surfacePrimaryFill"
                border={hasCOCEnabled ? '1px solid' : 'none'}
                borderColor="neutralPrimaryBorder"
              >
                <Flex
                  width="100%"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {/* Left title */}
                  <Text
                    textStyle="headingLine"
                    ml={!hasCOCEnabled ? 2 : undefined}
                  >
                    Decision History ({decisionHistoryCount})
                  </Text>

                  {/* Right actions */}
                  <Flex gap={3}>
                    <ButtonText
                      leftIcon={<FontAwesomeIcon icon={faEye} />}
                      onClick={onClick}
                      isDisabled={decisionHistoryCount === 0}
                    >
                      View decision history
                    </ButtonText>
                    <Box
                      borderRight="1px solid"
                      borderColor="neutralPrimaryBorder"
                    />
                    <Box ml={2} p={2}>
                      <FontAwesomeIcon
                        icon={isExpanded ? faMinus : faPlus}
                        color="blue"
                      />
                    </Box>
                  </Flex>
                </Flex>
              </AccordionButton>

              <AccordionPanel
                px={4}
                py={3}
                border={hasCOCEnabled ? '1px solid' : 'none'}
                borderTop={hasCOCEnabled ? 'none' : '1px solid'}
                borderColor="neutralPrimaryBorder"
                bgColor="surfaceSecondaryFill"
              >
                {decisionHistory?.length > 0 ? (
                  decisionHistory?.map(history => {
                    const decision = history?.decision;
                    const badgeKey =
                      decision?.type === 'RevisionRequested' &&
                      decision?.formPayload?.revisionSeverity
                        ? `${decision.formPayload.revisionSeverity}Revision`
                        : decision?.type;

                    return (
                      <OutlineCard
                        mt={3}
                        key={`${history.manuscriptId}-${
                          history.revisionNumber
                        }-${history.emailEventId ?? history.decision?.type}`}
                      >
                        <Flex gap={2} flexDirection="column" padding={2}>
                          <Flex
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Text textStyle="headingItem">
                              {`${history?.manuscriptId} | ${
                                history.revisionNumber === 0
                                  ? 'Original Submission'
                                  : `Revision - ${history.revisionNumber}`
                              } (`}

                              {dateFormat(history.dateEntered, 'mmm d, yyyy')}
                              {' to '}
                              {history.dateCompleted
                                ? dateFormat(
                                    history.dateCompleted,
                                    'mmm d, yyyy'
                                  )
                                : 'Ongoing'}
                              {')'}
                            </Text>
                          </Flex>

                          <Flex
                            justifyContent="space-between"
                            gap={2}
                            flexDirection="column"
                          >
                            <Flex gap={1} flexDirection="column">
                              <Flex alignItems="center">
                                <Flex gap={2} alignItems="center">
                                  <Text textStyle="headingLine">Decision:</Text>
                                  {decisionStatusBadges[badgeKey] || '--'}

                                  <Flex alignItems={'center'}>
                                    <Text textStyle="headingLine">
                                      Decision Email:
                                    </Text>
                                    <ButtonIcon
                                      icon={
                                        <FontAwesomeIcon icon={faEnvelope} />
                                      }
                                      ariaLabel="email"
                                      onClick={() => {
                                        setEmailEventId(
                                          history?.emailEventId ?? ''
                                        );
                                        setIsEmailModalOpen(true);
                                      }}
                                    />
                                  </Flex>
                                </Flex>
                              </Flex>
                              {history?.decision?.formPayload
                                ?.rejectionReason && (
                                <Flex gap={2} alignItems={'flex-start'}>
                                  <Text textStyle="headingLine">
                                    Rejection Reason:
                                  </Text>
                                  <Text lineHeight={'24px'} isTruncated>
                                    {
                                      history.decision.formPayload
                                        .rejectionReason
                                    }
                                  </Text>
                                </Flex>
                              )}
                              {history?.decision?.formPayload
                                ?.withdrawalReason && (
                                <Flex gap={2} alignItems={'flex-start'}>
                                  <Text textStyle="headingLine">
                                    Withdrawal Reason:
                                  </Text>
                                  <Text lineHeight={'24px'} isTruncated>
                                    {
                                      history.decision.formPayload
                                        .withdrawalReason
                                    }
                                  </Text>
                                </Flex>
                              )}
                              <Flex gap={2} alignItems={'flex-start'}>
                                <Text textStyle="headingLine">
                                  Response to Author:
                                </Text>
                                <Text isTruncated flex={1} minWidth={0}>
                                  {history?.decision?.formPayload
                                    ?.editorComment ?? '--'}
                                </Text>
                              </Flex>
                              <Flex
                                gap={2}
                                alignItems="center"
                                justifyContent={'space-between'}
                                color="gray.500"
                              >
                                <Text textStyle="textContext">
                                  Added by {history?.adminDetails?.firstName}{' '}
                                  {history?.adminDetails?.lastName} (
                                  {history?.adminDetails?.roleName}) on{' '}
                                  {dateFormat(
                                    history?.adminDetails?.decisionDate,
                                    'mmm d, yyyy'
                                  )}
                                </Text>

                                <ButtonText
                                  textStyle="headingLine"
                                  disabled={
                                    !history?.decision?.formPayload
                                      ?.editorAttachments ||
                                    history?.decision?.formPayload
                                      ?.editorAttachments?.length === 0
                                  }
                                  onClick={() =>
                                    setShowAttachments(
                                      history?.decision?.formPayload
                                        ?.editorAttachments || []
                                    )
                                  }
                                >
                                  Files (
                                  {history?.decision?.formPayload
                                    ?.editorAttachments?.length ?? 0}
                                  )
                                </ButtonText>
                              </Flex>
                            </Flex>
                            {(history.authorDetails !== null ||
                              history.authorResponseDetails !== null) && (
                              <>
                                {Divider}
                                <Flex flexDirection="column" gap={2}>
                                  <Flex gap={1} alignItems="center">
                                    <Text textStyle="headingLine">
                                      Author Response:
                                    </Text>
                                    <Text isTruncated flex={1} minWidth={0}>
                                      {' '}
                                      {
                                        history?.authorResponseDetails
                                          ?.authorResponse
                                      }
                                    </Text>
                                  </Flex>

                                  <Flex
                                    gap={2}
                                    alignItems="center"
                                    justifyContent={'space-between'}
                                    color="gray.500"
                                  >
                                    <Text textStyle="textContext">
                                      Added by{' '}
                                      {history?.authorDetails?.firstName}{' '}
                                      {history?.authorDetails?.lastName} (
                                      {history?.authorDetails?.roleName}) on{' '}
                                      {dateFormat(
                                        history?.authorDetails
                                          ?.revisionSubmittedDate,
                                        'mmm d, yyyy'
                                      )}
                                    </Text>
                                    <ButtonText
                                      textStyle="headingLine"
                                      disabled={
                                        !history?.authorResponseDetails
                                          ?.authorResponseAttachments ||
                                        history?.authorResponseDetails
                                          ?.authorResponseAttachments
                                          ?.length === 0
                                      }
                                      onClick={() =>
                                        setShowAttachments(
                                          history?.authorResponseDetails
                                            ?.authorResponseAttachments || []
                                        )
                                      }
                                    >
                                      Files (
                                      {history?.authorResponseDetails
                                        ?.authorResponseAttachments?.length ??
                                        0}
                                      )
                                    </ButtonText>
                                  </Flex>
                                </Flex>
                              </>
                            )}
                          </Flex>
                        </Flex>
                      </OutlineCard>
                    );
                  })
                ) : (
                  <Flex
                    justifyContent={!hasCOCEnabled ? 'flex-start' : 'center'}
                    alignItems="center"
                    padding={!hasCOCEnabled ? 2 : undefined}
                    minH={!hasCOCEnabled ? '100px' : undefined}
                  >
                    <Text textStyle="textContext">No decision history found</Text>
                  </Flex>
                )}
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </>
  );
};
